import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';

export default function Privatlivspolitik() {
  const { lang } = useLanguage();

  // SEO Metadata
  const seoTitle = lang === 'da' 
    ? 'Privatlivspolitik (GDPR) | Nordisk Psykiatri' 
    : 'Integritetspolicy (GDPR) | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Læs vores privatlivspolitik om hvordan vi indsamler, behandler og beskytter dine personoplysninger i overensstemmelse med GDPR.'
    : 'Läs vår integritetspolicy om hur vi samlar in, behandlar och skyddar dina personuppgifter i enlighet med GDPR.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  return (
    <div className="flex flex-col min-h-screen bg-sand-light">
      {/* HERO */}
      <FluidSubpageHero
        className="bg-[#f6f0e2]"
        style={{ backgroundColor: '#f6f0e2' }}
        eyebrow={lang === 'da' ? 'VILKÅR & POLITIKKER' : 'VILLKOR & POLICYER'}
        title={lang === 'da' ? 'Privatlivspolitik (GDPR)' : 'Integritetspolicy (GDPR)'}
        description={lang === 'da' 
          ? 'Hos Nordisk Psykiatri tager vi beskyttelsen af dine personoplysninger alvorligt. Denne privatlivspolitik beskriver, hvordan vi indsamler, bruger og beskytter dine oplysninger i overensstemmelse med databeskyttelsesforordningen (GDPR).'
          : 'Hos Nordisk Psykiatri tar vi skyddet av dina personuppgifter på allvar. Denna integritetspolicy beskriver hur vi samlar in, använder och skyddar dine uppgifter i enlighet med dataskyddsförordningen (GDPR).'}
      />

      {/* Clean, Simple Terms Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="mx-auto max-w-4xl px-6">
          {/* Sections */}
          <div className="space-y-12">
            
            {/* Section 1 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '1. Hvem er vi?' : '1. Vilka är vi?'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Nordisk Psykiatri er dataansvarlig for behandlingen af dine personoplysninger. Kontaktoplysninger fremgår nederst på siden.'
                    : 'Nordisk Psykiatri är personuppgiftsansvarig för behandlingen av dina personuppgifter. Kontaktuppgifter finns längst ner på sidan.'}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 2 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '2. Hvilke oplysninger indsamler vi?' : '2. Vilka uppgifter samlar vi in?'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Vi indsamler kun de oplysninger, der er nødvendige for at kunne tilbyde dig en sikker og professionel behandling. Det kan fx være:'
                    : 'Vi samlar endast in de uppgifter som är nödvändiga för att kunna erbjuda dig en säker och professionell behandling. Det kan till exempel vara:'}
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>{lang === 'da' ? 'Navn, adresse, telefonnummer og e-mail' : 'Namn, adress, telefonnummer och e-post'}</li>
                  <li>{lang === 'da' ? 'CPR-nummer' : 'Personnummer'}</li>
                  <li>{lang === 'da' ? 'Oplysninger om din fysiske og mentale sundhed, herunder journalnotater' : 'Uppgifter om din fysiska och mentala hälsa, inklusive journalanteckningar'}</li>
                  <li>{lang === 'da' ? 'Oplysninger om aftaler, betalinger og korrespondance' : 'Uppgifter om bokningar, betalningar och korrespondens'}</li>
                </ul>
                <p>
                  {lang === 'da'
                    ? 'Vi indsamler oplysninger direkte fra dig, medmindre andet er oplyst.'
                    : 'Vi samlar in uppgifter direkt från dig, om inte annat anges.'}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 3 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '3. Hvorfor behandler vi dine oplysninger?' : '3. Varför behandlar vi dina uppgifter?'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Vi behandler dine personoplysninger til følgende formål:'
                    : 'Vi behandlar dina personuppgifter för följande ändamål:'}
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>{lang === 'da' ? 'At kunne planlægge og gennemføre behandling' : 'Att kunna planera och genomföra behandling'}</li>
                  <li>{lang === 'da' ? 'At føre journal og sikre kontinuitet i behandlingen' : 'Att föra journal och säkerställa kontinuitet i behandlingen'}</li>
                  <li>{lang === 'da' ? 'At håndtere betaling og administration' : 'Att hantera betalning och administration'}</li>
                  <li>{lang === 'da' ? 'At overholde lovgivning, herunder bogføringsloven og sundhedsloven' : 'Att följa lagstiftning, inklusive bokföringslagen och hälso- och sjukvårdslagen'}</li>
                </ul>
                <p>
                  {lang === 'da'
                    ? 'Behandlingen sker på baggrund af dit samtykke og/eller fordi det er nødvendigt for at opfylde en aftale med dig eller overholde en retlig forpligtelse.'
                    : 'Behandlingen sker på grundval av ditt samtycke och/eller för att det är nödvändigt för att fullgöra ett avtal med dig eller uppfylla en rättslig förpliktelse.'}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 4 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '4. Opbevaring af oplysninger' : '4. Lagring av uppgifter'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Vi opbevarer dine oplysninger sikkert og kun så længe, det er nødvendigt i forhold til formålet med behandlingen, eller så længe lovgivningen kræver det.'
                    : 'Vi lagrar dina uppgifter säkert och endast så länge det är nödvändigt för ändamålet med behandlingen, eller så länge lagstiftningen kräver det.'}
                </p>
                <p>
                  {lang === 'da'
                    ? 'Journaloplysninger opbevares i henhold til gældende regler og slettes senest 5 år efter sidste kontakt, medmindre andet følger af lovgivningen.'
                    : 'Journaluppgifter lagras i enlighet med gällande regler och raderas senast 5 år efter senaste kontakt, om inte annat följer av lagstiftningen.'}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 5 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '5. Deling af oplysninger' : '5. Delning av uppgifter'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Dine oplysninger videregives ikke til tredjemand, medmindre det er nødvendigt for behandlingen (fx samarbejde med andre sundhedsfaglige) eller vi er forpligtet til det ved lov.'
                    : 'Dina uppgifter vidarebefordras inte till tredje part, såvida det inte är nödvändigt för behandlingen (t.ex. samarbete med annan hälso- och sjukvårdspersonal) eller om vi är skyldiga till det enligt lag.'}
                </p>
                <p>
                  {lang === 'da'
                    ? 'Vi anvender sikre samarbejdspartnere og databehandlere, som lever op til GDPR-kravene.'
                    : 'Vi använder säkra samarbetspartners och personuppgiftsbiträden som uppfyller GDPR-kraven.'}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 6 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '6. Dine rettigheder' : '6. Dina rättigheter'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Du har efter GDPR en række rettigheder, herunder:'
                    : 'Du har enligt GDPR ett antal rättigheter, inklusive:'}
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>{lang === 'da' ? 'Ret til indsigt i de oplysninger, vi behandler om dig' : 'Rätt till tillgång till de uppgifter vi behandlar om dig'}</li>
                  <li>{lang === 'da' ? 'Ret til berigtigelse af forkerte oplysninger' : 'Rätt till rättelse av felaktiga uppgifter'}</li>
                  <li>{lang === 'da' ? 'Ret til sletning (ret til at blive glemt)' : 'Rätt till radering (rätten att bli bortglömd)'}</li>
                  <li>{lang === 'da' ? 'Ret til begrænsning af behandling' : 'Rätt till begränsning av behandling'}</li>
                  <li>{lang === 'da' ? 'Ret til dataportabilitet' : 'Rätt till dataportabilitet'}</li>
                  <li>{lang === 'da' ? 'Ret til at gøre indsigelse mod behandlingen' : 'Rätt att göra invändningar mot behandlingen'}</li>
                </ul>
                <p>
                  {lang === 'da'
                    ? 'Du kan til enhver tid gøre brug af dine rettigheder ved at kontakte os.'
                    : 'Du kan när som helst utöva dina rättigheter genom att kontakta oss.'}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 7 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '7. Klager' : '7. Klagomål'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da' ? (
                    <>
                      Du har ret til at klage til Datatilsynet, hvis du mener, at vi ikke behandler dine personoplysninger korrekt. Læs mere på{' '}
                      <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-petrol-medium hover:text-petrol-dark transition-colors font-medium underline underline-offset-4">
                        www.datatilsynet.dk
                      </a>
                      .
                    </>
                  ) : (
                    <>
                      Du har rätt att klaga till Integritetsskyddsmyndigheten (IMY) om du anser att vi inte behandlar dina personuppgifter korrekt. Läs mer på{' '}
                      <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer" className="text-petrol-medium hover:text-petrol-dark transition-colors font-medium underline underline-offset-4">
                        www.imy.se
                      </a>
                      .
                    </>
                  )}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 8 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '8. Kontakt' : '8. Kontakt'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Hvis du har spørgsmål til vores behandling af dine personoplysninger, er du velkommen til at kontakte os:'
                    : 'Om du har frågor om vår behandling av dina personuppgifter är du välkommen att kontakta oss:'}
                </p>
                <div className="pl-4 border-l-2 border-petrol-medium/30 my-4 space-y-1">
                  <p className="font-medium text-petrol-dark">Nordisk Psykiatri</p>
                  <p>Vesterbrogade 37, 1. tv</p>
                  <p>1620 København V</p>
                  <p>
                    {lang === 'da' ? 'Telefon: ' : 'Telefon: '}
                    <a href="tel:+4523957707" className="text-petrol-medium hover:text-petrol-dark transition-colors font-medium">
                      +45 23 95 77 07
                    </a>
                  </p>
                  <p>
                    {lang === 'da' ? 'E-mail: ' : 'E-post: '}
                    <a href="mailto:kontakt@nordiskpsykiatri.dk" className="text-petrol-medium hover:text-petrol-dark transition-colors font-medium">
                      kontakt@nordiskpsykiatri.dk
                    </a>
                  </p>
                </div>
                <p className="text-sm text-text-dark/50 mt-6 italic">
                  {lang === 'da'
                    ? 'Denne privatlivspolitik er senest opdateret den 15. maj 2024.'
                    : 'Denna integritetspolicy uppdaterades senast den 15 maj 2024.'}
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
